import {
  getMedicationArchiveMessage,
  prepareMedicationArchive,
  prepareMedicationDraftSave,
} from "./useProfileMedicationEditor";

const defaultConfirmArchive = (message) => (
  typeof window === "undefined" || typeof window.confirm !== "function"
    ? true
    : window.confirm(message)
);

export const saveProfileMedicationDraft = async ({
  medicationDetails = [],
  medicationDraft = {},
  editingMedicationId = null,
  childId = "",
  createdBy = "",
  canEditMedication = true,
  permissionMessage = "You don't have permission to edit medications for this child.",
  childNotReadyMessage = "Child profile is not ready yet.",
  setSubmitError,
  setMedicationDetails,
  clearMedicationDraft,
  persistMedicationRecord,
  syncMedicationDetails,
  persistMedicationTimelineEvent,
  onNonCriticalError,
  markCurrentSnapshot,
  clearDraftOnSuccess = true,
  syncErrorMessage = "Medication saved, but child profile sync failed:",
} = {}) => {
  if (!canEditMedication) {
    setSubmitError?.(permissionMessage);
    return null;
  }

  if (!childId) {
    setSubmitError?.(childNotReadyMessage);
    return null;
  }

  const preparedSave = prepareMedicationDraftSave({
    medicationDetails,
    medicationDraft,
    editingMedicationId,
    childId,
    createdBy,
  });
  const { targetEntry, nextMedicationDetails, medicationRecord, previousMedication } = preparedSave;

  if (!String(targetEntry.name || "").trim()) {
    setSubmitError?.("Please enter a medication name.");
    return null;
  }

  let medicationWasSaved = false;
  try {
    const savedMedication = await persistMedicationRecord?.({
      childId,
      medication: medicationRecord,
      status: "saved",
      previousMedication,
      ...(persistMedicationTimelineEvent ? { createTimelineEvent: false } : {}),
    });
    medicationWasSaved = true;

    if (syncMedicationDetails) {
      await syncMedicationDetails(nextMedicationDetails);
    }

    setMedicationDetails?.(nextMedicationDetails);
    markCurrentSnapshot?.({
      medicationDetails: nextMedicationDetails,
    });

    const nonCriticalErrors = [];
    if (persistMedicationTimelineEvent) {
      try {
        await persistMedicationTimelineEvent({
          previousMedication,
          medication: savedMedication || medicationRecord,
        });
      } catch (error) {
        console.error("Medication saved, but its Timeline event failed:", error);
        const issue = { stage: "timeline", error };
        nonCriticalErrors.push(issue);
        onNonCriticalError?.(issue);
      }
    }

    if (clearDraftOnSuccess) {
      clearMedicationDraft?.();
    }

    return {
      ...preparedSave,
      status: "saved",
      nonCriticalErrors,
    };
  } catch (error) {
    const message = medicationWasSaved
      ? "Medication saved, but the Profile could not be updated. Please try again."
      : error?.message || "Could not save this medication row right now.";
    console.error(medicationWasSaved ? syncErrorMessage : "Failed to save medication detail:", error);
    setSubmitError?.(message);
    return medicationWasSaved
      ? {
          ...preparedSave,
          status: "partial",
          medicationSaved: true,
          profileSynced: false,
          error: message,
        }
      : null;
  }
};

export const archiveProfileMedicationDetail = async ({
  medicationDetails = [],
  entry = {},
  childId = "",
  createdBy = "",
  archivedBy = "",
  archived = true,
  canEditMedication = true,
  requiresChildId = false,
  permissionMessage = "You don't have permission to edit medications for this child.",
  setSubmitError,
  setMedicationDetails,
  clearMedicationDraft,
  editingMedicationId = null,
  confirmArchive = defaultConfirmArchive,
  persistMedicationArchiveRecord,
  syncMedicationDetails,
  markCurrentSnapshot,
  syncErrorMessage = "Medication archive saved, but child profile sync failed:",
} = {}) => {
  if (!canEditMedication) {
    setSubmitError?.(permissionMessage);
    return null;
  }

  if ((requiresChildId && !childId) || !entry?.id) {
    return null;
  }

  const confirmed = confirmArchive(getMedicationArchiveMessage(entry, archived));

  if (!confirmed) {
    return null;
  }

  const preparedArchive = prepareMedicationArchive({
    medicationDetails,
    entry,
    childId,
    createdBy,
    archived,
    archivedBy,
  });
  const { nextMedicationDetails, medicationRecord } = preparedArchive;

  setMedicationDetails?.(nextMedicationDetails);

  try {
    await persistMedicationArchiveRecord?.({
      childId,
      medication: medicationRecord,
      archived,
    });

    if (syncMedicationDetails) {
      void Promise.resolve(syncMedicationDetails(nextMedicationDetails)).catch((error) => {
        console.error(syncErrorMessage, error);
      });
    }

    markCurrentSnapshot?.({
      medicationDetails: nextMedicationDetails,
    });

    if (editingMedicationId === entry.id) {
      clearMedicationDraft?.();
    }

    return preparedArchive;
  } catch (error) {
    console.error("Failed to archive medication detail:", error);
    setSubmitError?.(error?.message || "Could not update this medication right now.");
    return null;
  }
};
